import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { GetAllMerchants } from '../../network/Merchants';
import {
  GetAllTickets,
  GetTicketByFreshdeskId,
  GetTicketById,
  GetTicketStats,
  GetTicketTimeline
} from '../network/Ticket';
import {
  computeHasNextTicketListPage,
  normalizeTicketByIdResponse,
  normalizeTimelineForUi,
  parseTicketStatsPayload,
  pickTicketFromFreshdeskTicketsResponse,
  rowFreshdeskDisplayId,
  ticketListPaginationState,
  ticketMatchesSearchQuery
} from '../utils/ticketManagementUtils';

import { getDateInddMMYYYY, getDateINYMD } from '../../utils/formatTime';
import { parseValidationErrorMessage } from '../../utils/Utilities';
import { TICKET_MANAGEMENT_OPTIONS } from '../utils/ticketManagementConstants';

function isValidMerchantSearchVpa(vpa) {
  if (typeof vpa !== 'string') {
    return false;
  }

  const normalized = vpa.trim().toLowerCase();

  const vpaRegex =
    /^(?![.-])[a-z0-9.-]{2,256}(?<![._-])@[a-z0-9]{2,64}$/;

  return vpaRegex.test(normalized);
}

const TICKET_LIST_PAGE_SIZE = 10;

const TICKET_LIST_INITIAL_PAGE = 1;

const MERCHANT_TICKET_SEARCH_LIMIT = 100;

function buildTicketListWhere({ status, searchText, merchantId }) {
  return {
    ...(status !== 'All' && { status }),
    ...(String(searchText || '').trim() && {
      freshdeskTicketId: String(searchText).trim()
    }),
    ...(merchantId && { merchantId })
  };
}

const initialTicketListQuery = () => ({
  page: TICKET_LIST_INITIAL_PAGE,
  status: 'All',
  searchText: '',
  merchantId: null,
  startDate: '',
  endDate: ''
});


function ticketListQueryReducer(state, action) {
  switch (action.type) {
    case 'APPLY_DATE_RANGE':
      return {
        ...state,
        startDate: action.startDate,
        endDate: action.endDate,
        page: TICKET_LIST_INITIAL_PAGE
      };
    case 'CLEAR_DATE_RANGE':
      return {
        ...state,
        startDate: '',
        endDate: '',
        page: TICKET_LIST_INITIAL_PAGE
      };
    case 'CHANGE_PAGE': {
      let newPage = state.page;
      if (action.direction === 'NEXT') newPage += 1;
      else if (action.direction === 'PREV') newPage -= 1;
      if (action.customPage !== undefined) newPage = action.customPage;
      return { ...state, page: newPage };
    }
    case 'SET_STATUS':
      return {
        ...state,
        status: action.status,
        page: TICKET_LIST_INITIAL_PAGE
      };
    case 'SUBMIT_TICKET_ID_SEARCH':
      return {
        ...state,
        searchText: action.searchText,
        page: TICKET_LIST_INITIAL_PAGE
      };
    case 'CLEAR_MERCHANT_FILTER':
      return {
        ...state,
        merchantId: null,
        page: TICKET_LIST_INITIAL_PAGE
      };
    case 'SET_MERCHANT_ID':
      return {
        ...state,
        merchantId: action.merchantId,
        page: TICKET_LIST_INITIAL_PAGE
      };
    case 'SET_PAGE':
      return { ...state, page: action.page };
    default:
      return state;
  }
}

export function useTicketManagement(sessionUserData) {
  const navigate = useNavigate();

  const [openConfirm, setOpenConfirm] = useState(false);

  const [dialogConfig, setDialogConfig] = useState({
    loading: false,
    message: '',
    note: false,
    onConfirm: () => { },
    onCancel: () => setOpenConfirm(false),
    confirmText: ''
  });

  const [isDateRangeDialogOpen, setIsDateRangeDialogOpen] = useState(false);
  const [createTicketOpen, setCreateTicketOpen] = useState(false);

  const [selectedTicketForAction, setSelectedTicketForAction] = useState({
    ticket: {},
    action: null
  });

  const [ticketStats, setTicketStats] = useState({});
  const [ticketStatsLoading, setTicketStatsLoading] = useState(false);

  const [isSnackBarAlertOpen, setIsSnackBarAlertOpen] = useState(false);

  const [snackBarMetas, setSnackBarMetas] = useState({
    title: '',
    message: '',
    severity: 'info',
    handleClose: null
  });

  const [range, setRange] = useState('Today');
  const [customDateSelectedString, setCustomDateSelectedString] = useState('');

  const ticketListDataRef = useRef([]);
  const ticketListQueryRef = useRef(initialTicketListQuery());
  const expandedDetailFetchGenRef = useRef(0);
  const ticketListAbortControllerRef = useRef(null);
  const merchantSearchAbortControllerRef = useRef(null);

  const [searchText, setSearchText] = useState('');

  const [merchantTicketSearchType, setMerchantTicketSearchType] = useState('name');
  const [merchantTicketSearchValue, setMerchantTicketSearchValue] = useState('');
  const [merchantTicketSearchResults, setMerchantTicketSearchResults] = useState([]);
  const [merchantTicketSearchLoading, setMerchantTicketSearchLoading] = useState(false);

  const [ticketListData, setTicketListData] = useState([]);
  const [ticketListLoading, setTicketListLoading] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(true);

  const [ticketListQuery, dispatchTicketListQuery] = useReducer(
    ticketListQueryReducer,
    undefined,
    initialTicketListQuery
  );

  // -------------------------------
  // EXPANDED ROW STATE
  // -------------------------------
  const [expandedTicket, setExpandedTicket] = useState(null);
  const [expandedDetailLoading, setExpandedDetailLoading] = useState(false);
  const [conversations, setConversations] = useState({});
  const [singleTicketData, setSingleTicketData] = useState({});

  useEffect(() => {
    ticketListDataRef.current = ticketListData;
  }, [ticketListData]);

  useEffect(() => {
    ticketListQueryRef.current = ticketListQuery;
  }, [ticketListQuery]);

  // -------------------------------
  // TICKET STATS
  // -------------------------------
  const fetchTicketStats = useCallback((rangeValue, startDate, endDate) => {
    if (rangeValue === 'Between Dates' && !startDate) {
      setIsDateRangeDialogOpen(true);
      return;
    }

    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;

    setTicketStatsLoading(true);

    GetTicketStats(params)
      .then((res) => setTicketStats(parseTicketStatsPayload(res)))
      .catch(() => { })
      .finally(() => setTicketStatsLoading(false));
  }, []);

  useEffect(() => {
    fetchTicketStats();
  }, [fetchTicketStats]);

  const fetchTickets = useCallback((currentPage, limit, type, where, startDate, endDate, submittedSearchText = '') => {
    ticketListAbortControllerRef.current?.abort();
    const controller = new AbortController();
    ticketListAbortControllerRef.current = controller;

    setTicketListLoading(true);

    GetAllTickets(currentPage, limit, where, startDate, endDate, controller.signal)
      .then((res) => {
        const data = res?.data?.data?.tickets ?? res?.data?.data ?? [];

        const hasNext = computeHasNextTicketListPage(res, data, limit);
        ticketListPaginationState.hasNextPage = hasNext;
        setHasNextPage(hasNext);

        if (type === TICKET_MANAGEMENT_OPTIONS.LOAD) {
          setTicketListData((prev) => [...prev, ...data]);
        } else if (type === TICKET_MANAGEMENT_OPTIONS.SEARCH) {
          setTicketListData(
            submittedSearchText
              ? data.filter((t) => ticketMatchesSearchQuery(t, submittedSearchText))
              : data
          );
        }
      })
      .catch((err) => {
        if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError') return;
        setHasNextPage(false);
        ticketListPaginationState.hasNextPage = false;
        setTicketListData([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setTicketListLoading(false);
        }
      });
  }, []);

  useEffect(() => {
    fetchTickets(
      ticketListQuery.page,
      TICKET_LIST_PAGE_SIZE,
      TICKET_MANAGEMENT_OPTIONS.SEARCH,
      buildTicketListWhere(ticketListQuery),
      ticketListQuery.startDate,
      ticketListQuery.endDate,
      ticketListQuery.searchText
    );
  }, [ticketListQuery, fetchTickets]);

  // -------------------------------
  // DATE FILTER
  // -------------------------------
  const onDateRangeSelectedAction = (fromDate, toDate) => {
    if (!fromDate || !toDate) {
      setSnackBarMetas((prev) => ({
        ...prev,
        title: "You haven't selected the Date Range.",
        severity: 'warning'
      }));
      setIsSnackBarAlertOpen(true);
      setRange('Today');
      fetchTicketStats('Today');
      return null;
    }

    const startDate = getDateINYMD(fromDate);
    const endDate = getDateINYMD(toDate);

    setCustomDateSelectedString(
      `${getDateInddMMYYYY(fromDate)} -to- ${getDateInddMMYYYY(toDate)}`
    );

    fetchTicketStats('Between Dates', startDate, endDate);

    dispatchTicketListQuery({
      type: 'APPLY_DATE_RANGE',
      startDate,
      endDate
    });

    return { startDate, endDate };
  };

  const clearTicketDateFilter = () => {
    setRange('Today');
    setCustomDateSelectedString('');
    fetchTicketStats();

    dispatchTicketListQuery({ type: 'CLEAR_DATE_RANGE' });
  };

  // -------------------------------
  // PAGINATION & FILTERS
  // -------------------------------
  const handleChangePage = (type, customPage) => {
    dispatchTicketListQuery({
      type: 'CHANGE_PAGE',
      direction: type === 'NEXT' ? 'NEXT' : 'PREV',
      customPage
    });
  };

  const handleStatusClickChange = (event) => {
    dispatchTicketListQuery({
      type: 'SET_STATUS',
      status: event.target.value
    });
  };

  // -------------------------------
  // TICKET ID SEARCH
  // -------------------------------
  const handleTicketSearchSubmit = () => {
    setExpandedTicket(null);
    setConversations({});
    setSingleTicketData({});

    dispatchTicketListQuery({
      type: 'SUBMIT_TICKET_ID_SEARCH',
      searchText
    });
  };

  // -------------------------------
  // MORE MENU
  // -------------------------------
  const handleTicketsMoreMenuClick = (action, ticketData) => {
    if (action === 'update_ticket') {
      setCreateTicketOpen(true);
      setSelectedTicketForAction({
        ticket: ticketData,
        action: 'update_ticket'
      });
    }

    if (action === 'visit_merchant') {
      if (!ticketData?.merchantId) {
        setSnackBarMetas({
          title: 'No Merchant ID found',
          severity: 'error'
        });
        setIsSnackBarAlertOpen(true);
        return;
      }
      GetAllMerchants(TICKET_LIST_INITIAL_PAGE, TICKET_LIST_PAGE_SIZE, {
        id: ticketData?.merchantId
      }).then((res) => {
        if (res?.data?.data?.length > 0 && res?.data?.data?.length === 1) {
          navigate(
            `/dashboard/soundboxdevicedetails/${res?.data?.data?.[0]?.id}/${sessionUserData?.user?.organizationId}`
          );
        } else {
          setSnackBarMetas({
            title: 'No Merchant found',
            severity: 'error'
          });
          setIsSnackBarAlertOpen(true);
        }
      }).catch((err) => {
        setSnackBarMetas({
          title: parseValidationErrorMessage(err),
          severity: 'error'
        });
        setIsSnackBarAlertOpen(true);
      });
    }
  };

  // -------------------------------
  // EXPANDED ROW
  // -------------------------------
  const fetchTicketData = (id) => {
    GetTicketTimeline(id)
      .then((res) => {
        setConversations({ [id]: normalizeTimelineForUi(res) });
      })
      .catch(() => setConversations({}));
  };

  const fetchExpandedRowDetails = (row) => {
    expandedDetailFetchGenRef.current += 1;
    const gen = expandedDetailFetchGenRef.current;

    setExpandedDetailLoading(true);
    setSingleTicketData({});

    const finish = () => {
      if (expandedDetailFetchGenRef.current === gen) {
        setExpandedDetailLoading(false);
      }
    };

    const loadInternal = () =>
      GetTicketById(row.id)
        .then((res) => {
          if (expandedDetailFetchGenRef.current === gen) {
            setSingleTicketData(normalizeTicketByIdResponse(res));
          }
        })
        .catch(() => setSingleTicketData({}));

    const fdId = rowFreshdeskDisplayId(row);

    if (fdId) {
      GetTicketByFreshdeskId(fdId)
        // eslint-disable-next-line consistent-return -- fallback chain returns loadInternal promise
        .then((res) => {
          const ticket = pickTicketFromFreshdeskTicketsResponse(res);
          if (ticket) setSingleTicketData(ticket);
          else return loadInternal();
        })
        .catch(loadInternal)
        .finally(finish);
    } else {
      loadInternal().finally(finish);
    }
  };

  // -------------------------------
  // COMMON ACTIONS
  // -------------------------------
  const reloadTicketList = useCallback(() => {
    const q = ticketListQueryRef.current;
    fetchTickets(
      q.page,
      TICKET_LIST_PAGE_SIZE,
      TICKET_MANAGEMENT_OPTIONS.SEARCH,
      buildTicketListWhere(q),
      q.startDate,
      q.endDate,
      q.searchText
    );
  }, [fetchTickets]);

  const onReload = () => {
    reloadTicketList();

    if (ticketListQuery.startDate && ticketListQuery.endDate) {
      fetchTicketStats('Between Dates', ticketListQuery.startDate, ticketListQuery.endDate);
    } else {
      fetchTicketStats();
    }
  };

  const onTicketDateRangeSelected = (from, to) => {
    onDateRangeSelectedAction(from, to);
  };

  const onClearTicketDateFilter = () => {
    clearTicketDateFilter();
  };

  // -------------------------------
  // MERCHANT SEARCH
  // -------------------------------
  const clearMerchantTicketSearch = () => {
    merchantSearchAbortControllerRef.current?.abort();
    setMerchantTicketSearchValue('');
    setMerchantTicketSearchResults([]);
    setMerchantTicketSearchLoading(false);
  };

  const handleMerchantTicketSearchTypeChange = (nextType) => {
    setMerchantTicketSearchType(nextType);
    clearMerchantTicketSearch();
    dispatchTicketListQuery({ type: 'CLEAR_MERCHANT_FILTER' });
  };

  const fetchTicketsForMerchantId = (merchantId) => {
    setExpandedTicket(null);
    setConversations({});
    setSingleTicketData({});
    dispatchTicketListQuery({ type: 'SET_MERCHANT_ID', merchantId });
  };

  const handleMerchantTicketSearchSubmit = async (overrideValue) => {
    const raw = overrideValue !== undefined ? overrideValue : merchantTicketSearchValue;
    const entered = String(raw ?? '').trim();

    setExpandedTicket(null);
    setConversations({});
    setSingleTicketData({});

    if (!entered) {
      clearMerchantTicketSearch();
      dispatchTicketListQuery({ type: 'CLEAR_MERCHANT_FILTER' });
      return;
    }

    let merchantWhere = null;
    if (merchantTicketSearchType === 'vpa') {
      if (!isValidMerchantSearchVpa(entered)) {
        setSnackBarMetas((prev) => ({
          ...prev,
          title: 'Invalid VPA format.',
          severity: 'warning'
        }));
        setIsSnackBarAlertOpen(true);
        return;
      }
      merchantWhere = { vpa: entered.toLowerCase() };
    } else if (merchantTicketSearchType === 'name') {
      merchantWhere = { name: entered };
    } else if (merchantTicketSearchType === 'phone') {
      merchantWhere = { phone: entered };
    }

    merchantSearchAbortControllerRef.current?.abort();
    const controller = new AbortController();
    merchantSearchAbortControllerRef.current = controller;

    setMerchantTicketSearchLoading(true);
    setMerchantTicketSearchResults([]);

    try {
      const res = await GetAllMerchants(
        TICKET_LIST_INITIAL_PAGE,
        MERCHANT_TICKET_SEARCH_LIMIT,
        merchantWhere,
        controller.signal
      );

      const merchants = res?.data?.data ?? [];
      if (merchants.length === 0) {
        setSnackBarMetas((prev) => ({
          ...prev,
          title: 'No merchants found for this search.',
          severity: 'warning'
        }));
        setIsSnackBarAlertOpen(true);
        return;
      }
      if (merchants.length === 1) {
        fetchTicketsForMerchantId(merchants[0].id);
        return;
      }
      setMerchantTicketSearchResults(merchants);
    } catch (error) {
      if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError') return;
      setSnackBarMetas((prev) => ({
        ...prev,
        title: parseValidationErrorMessage(error),
        severity: 'error'
      }));
      setIsSnackBarAlertOpen(true);
    } finally {
      if (!controller.signal.aborted) {
        setMerchantTicketSearchLoading(false);
      }
    }
  };

  const handleMerchantTicketSelectId = (merchantId) => {
    if (!merchantId) return;
    fetchTicketsForMerchantId(merchantId);
  };

  const merchantTicketShowMerchantPicker =
    merchantTicketSearchResults.length > 1;

  const handleClearMerchantTicketSearch = () => {
    clearMerchantTicketSearch();
    dispatchTicketListQuery({ type: 'CLEAR_MERCHANT_FILTER' });
  };

  const ticketDateFilter = {
    startDate: ticketListQuery.startDate,
    endDate: ticketListQuery.endDate
  };

  return {
    // UI
    openConfirm,
    setOpenConfirm,
    dialogConfig,
    setDialogConfig,
    isDateRangeDialogOpen,
    setIsDateRangeDialogOpen,
    createTicketOpen,
    setCreateTicketOpen,
    selectedTicketForAction,
    setSelectedTicketForAction,
    snackBarMetas,
    setSnackBarMetas,
    isSnackBarAlertOpen,
    setIsSnackBarAlertOpen,

    // stats
    ticketStats,
    ticketStatsLoading,

    // filters
    range,
    setRange,
    customDateSelectedString,
    ticketDateFilter,

    // list
    page: ticketListQuery.page,
    setPage: (newPage) => dispatchTicketListQuery({ type: 'SET_PAGE', page: newPage }),
    searchText,
    setSearchText,
    status: ticketListQuery.status,
    ticketListData,
    ticketListLoading,
    hasNextPage,

    // actions
    handleChangePage,
    handleStatusClickChange,
    handleTicketsMoreMenuClick,
    handleTicketSearchSubmit,
    merchantTicketSearchType,
    merchantTicketSearchValue,
    setMerchantTicketSearchValue,
    merchantTicketSearchResults,
    merchantTicketSearchLoading,
    merchantTicketSelectedId: ticketListQuery.merchantId,
    merchantTicketShowMerchantPicker,
    handleMerchantTicketSearchTypeChange,
    handleMerchantTicketSearchSubmit,
    handleMerchantTicketSelectId,
    handleClearMerchantTicketSearch,
    fetchTickets,
    reloadTicketList,
    onReload,
    onTicketDateRangeSelected,
    onClearTicketDateFilter,

    // expanded
    expandedDetailFetchGenRef,
    expandedTicket,
    setExpandedTicket,
    expandedDetailLoading,
    setExpandedDetailLoading,
    conversations,
    setConversations,
    singleTicketData,
    setSingleTicketData,
    fetchTicketData,
    fetchExpandedRowDetails
  };
}
